import { type ComponentType, lazy } from 'react'

import { isNative } from '@/shared/api/platform'

import { recordClientEvent } from './clientEvents'

/**
 * Carga de pantallas que no puede dejar la app colgada (spec 2026-11-02-atasco-navegacion-app).
 *
 * Cada pantalla es un trozo de código que se pide al navegar. React Router navega dentro de una
 * transición: mientras el trozo no llega, deja la pantalla anterior, y como las transiciones
 * pendientes se encadenan, una carga que no termina nunca deja muerta TODA la navegación (en la
 * app de iPhone, al volver de segundo plano: «toco Inicio, Explorar, Crear… y nada»). `import()`
 * no tiene tiempo límite, así que se lo ponemos: si el trozo no llega a tiempo, falla con
 * {@link ChunkLoadTimeoutError} y la pantalla de error de la ruta lo trata como un trozo caducado
 * (`staleChunk`): recarga la app una vez, que es lo que el usuario hacía a mano.
 *
 * No se reintenta: el navegador reutiliza la petición del módulo que ya está en curso, así que un
 * segundo `import()` esperaría a la misma petición atascada. Solo una recarga la libera.
 *
 * Cada atasco se cuenta (`CHUNK_LOAD_STALLED`) para saber cuánto pasa.
 */

/** En la app los trozos son ficheros locales: si no llegan en segundos, no van a llegar. */
export const NATIVE_CHUNK_TIMEOUT_MS = 8_000
/** En la web hay red de por medio: margen para una conexión lenta. */
export const WEB_CHUNK_TIMEOUT_MS = 20_000

export class ChunkLoadTimeoutError extends Error {
  constructor() {
    super('Timed out loading module chunk')
    this.name = 'ChunkLoadTimeoutError'
  }
}

export function chunkTimeoutMs(): number {
  return isNative() ? NATIVE_CHUNK_TIMEOUT_MS : WEB_CHUNK_TIMEOUT_MS
}

export function loadChunk<T>(
  importer: () => Promise<T>,
  timeoutMs: number = chunkTimeoutMs(),
): Promise<T> {
  // La ruta a la que se iba (al saltar el tiempo límite puede que ya se esté en otra).
  const path = window.location.pathname
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      recordClientEvent({ type: 'CHUNK_LOAD_STALLED', path })
      reject(new ChunkLoadTimeoutError())
    }, timeoutMs)
    importer().then(
      (module) => {
        clearTimeout(timer)
        resolve(module)
      },
      (error: unknown) => {
        // Un fallo de verdad (trozo que ya no existe tras un despliegue) no es un atasco: lo
        // trata `staleChunk` como siempre.
        clearTimeout(timer)
        reject(error instanceof Error ? error : new Error(String(error)))
      },
    )
  })
}

/** `React.lazy` con {@link loadChunk}: así se declaran todas las pantallas del router. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- misma firma que React.lazy
export function lazyPage<T extends ComponentType<any>>(importer: () => Promise<{ default: T }>) {
  return lazy(() => loadChunk(importer))
}
